import type { FormEvent } from "react";
import { useCallback, useEffect, useState } from "react";
import {
  ChevronRight,
  Home,
  LoaderCircle,
  Map as MapIcon,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Plus,
  RefreshCw,
  Search,
  X,
} from "lucide-react";

import {
  createClient,
  createProperty,
  getClient,
  getClients,
  getSquareCustomers,
  updateClient,
  updateProperty,
} from "./api";
import type {
  Client,
  ClientDetail,
  ClientInput,
  ClientStatus,
  Property,
  PropertyInput,
  PropertyType,
  SquareCustomer,
} from "./types";

export function ClientsPage({ canWrite }: { canWrite: boolean }) {
  const [clients, setClients] = useState<Client[]>([]);
  const [cursor, setCursor] = useState<string | null>();
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [panel, setPanel] = useState<string | "new">();
  const [squareCustomers, setSquareCustomers] = useState<SquareCustomer[]>([]);
  const [squareCursor, setSquareCursor] = useState<string | null>();
  const [squareTotal, setSquareTotal] = useState<number | null>();
  const [squareLoading, setSquareLoading] = useState(true);
  const [squareFailed, setSquareFailed] = useState(false);

  const load = useCallback(
    async (next?: string) => {
      setLoading(true);
      try {
        const page = await getClients({ cursor: next, search });
        setClients((current) => (next ? [...current, ...page.items] : page.items));
        setCursor(page.nextCursor);
        setFailed(false);
      } catch {
        setFailed(true);
      } finally {
        setLoading(false);
      }
    },
    [search],
  );

  useEffect(() => void load(), [load]);

  const loadSquare = useCallback(async (next?: string) => {
    setSquareLoading(true);
    try {
      const page = await getSquareCustomers({ cursor: next });
      setSquareCustomers((current) => (next ? [...current, ...page.items] : page.items));
      setSquareCursor(page.nextCursor);
      setSquareTotal(page.totalCount);
      setSquareFailed(page.state !== "available");
    } catch {
      setSquareFailed(true);
    } finally {
      setSquareLoading(false);
    }
  }, []);

  useEffect(() => void loadSquare(), [loadSquare]);

  return (
    <div className="page-content clients-page">
      <header className="page-intro client-intro">
        <div>
          <span className="eyebrow lime">Customer care</span>
          <h1>Clients and the homes they trust us with.</h1>
          <p>
            Keep contact details, property needs, and access instructions organized in one private
            workspace.
          </p>
        </div>
        {canWrite ? (
          <button className="primary-button compact-action" onClick={() => setPanel("new")}>
            <Plus /> Add client
          </button>
        ) : null}
      </header>

      <section className="content-card square-customers-card">
        <div className="section-heading client-toolbar">
          <div>
            <span className="eyebrow">Square · read-only</span>
            <h2>Square customers</h2>
            <p className="section-caption">
              Live customer records from Square. These records are visible here for context and are
              never edited from TLC Operations.
            </p>
          </div>
          <button
            className="secondary-button small-action"
            onClick={() => void loadSquare()}
            disabled={squareLoading}
          >
            <RefreshCw className={squareLoading ? "spin" : undefined} /> Refresh Square
          </button>
        </div>

        <div className="square-customer-summary">
          <strong>{squareTotal == null ? "—" : squareTotal.toLocaleString("en-US")}</strong>
          <span>customers in Square</span>
        </div>
        <SquareCustomerMap customers={squareCustomers} />
        {squareFailed ? <SquareCustomerLoadError onRetry={() => void loadSquare()} /> : null}
        <SquareCustomerTable customers={squareCustomers} loading={squareLoading} />
        {squareCursor ? (
          <button
            className="secondary-button load-more"
            onClick={() => void loadSquare(squareCursor)}
            disabled={squareLoading}
          >
            {squareLoading ? <LoaderCircle className="spin" /> : null} Load more Square customers
          </button>
        ) : null}
      </section>

      <section className="content-card">
        <div className="section-heading client-toolbar">
          <div>
            <span className="eyebrow">Directory</span>
            <h2>All clients</h2>
          </div>
          <form
            className="search-shell real-search"
            onSubmit={(event) => {
              event.preventDefault();
              setSearch(searchDraft.trim());
            }}
          >
            <Search />
            <input
              aria-label="Search clients"
              placeholder="Name, email, or phone"
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
            />
          </form>
        </div>

        {failed ? <ClientLoadError onRetry={() => void load()} /> : null}
        <ClientTable
          clients={clients}
          loading={loading && clients.length === 0}
          onOpen={setPanel}
        />
        {cursor ? (
          <button className="secondary-button load-more" onClick={() => void load(cursor)}>
            {loading ? <LoaderCircle className="spin" /> : null} Load more
          </button>
        ) : null}
      </section>

      {panel ? (
        <ClientPanel
          clientId={panel === "new" ? undefined : panel}
          canWrite={canWrite}
          onClose={() => setPanel(undefined)}
          onChanged={() => void load()}
          onCreated={(id) => setPanel(id)}
        />
      ) : null}
    </div>
  );
}

function ClientTable({
  clients,
  loading,
  onOpen,
}: {
  clients: Client[];
  loading: boolean;
  onOpen: (id: string) => void;
}) {
  if (loading) {
    return (
      <div className="table-state">
        <LoaderCircle className="spin" /> Loading clients…
      </div>
    );
  }
  if (clients.length === 0) {
    return <div className="table-state">No clients match this search yet.</div>;
  }

  return (
    <div className="table-wrap">
      <table className="mobile-card-table">
        <thead>
          <tr>
            <th>Client</th>
            <th>Phone</th>
            <th>Properties</th>
            <th>Status</th>
            <th>Updated</th>
            <th>
              <span className="sr-only">Open</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {clients.map((client) => (
            <tr key={client.id}>
              <td data-label="Client">
                <strong>{client.name}</strong>
                <span>{client.email}</span>
              </td>
              <td data-label="Phone">{client.phone}</td>
              <td data-label="Properties">{client.propertyCount}</td>
              <td data-label="Status">
                <ClientStatusBadge status={client.status} />
              </td>
              <td data-label="Updated">{formatShortDate(client.updatedAt)}</td>
              <td data-label="">
                <button
                  className="row-action"
                  aria-label={`Open ${client.name}`}
                  onClick={() => onOpen(client.id)}
                >
                  <ChevronRight />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SquareCustomerTable({
  customers,
  loading,
}: {
  customers: SquareCustomer[];
  loading: boolean;
}) {
  if (loading && customers.length === 0) {
    return (
      <div className="table-state">
        <LoaderCircle className="spin" /> Loading Square customers…
      </div>
    );
  }
  if (customers.length === 0) {
    return <div className="table-state">No Square customer records are available.</div>;
  }

  return (
    <div className="table-wrap">
      <table className="mobile-card-table">
        <thead>
          <tr>
            <th>Customer</th>
            <th>Phone</th>
            <th>Company</th>
            <th>Address</th>
            <th>Added</th>
          </tr>
        </thead>
        <tbody>
          {customers.map((customer) => (
            <tr key={customer.id}>
              <td data-label="Customer">
                <strong>{customer.name}</strong>
                {customer.email ? <span>{customer.email}</span> : null}
              </td>
              <td data-label="Phone">{customer.phone ?? "—"}</td>
              <td data-label="Company">{customer.company ?? "—"}</td>
              <td data-label="Address">{formatCustomerAddress(customer.address)}</td>
              <td data-label="Added">{formatCustomerDate(customer.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SquareCustomerMap({ customers }: { customers: SquareCustomer[] }) {
  const [selectedId, setSelectedId] = useState<string>();
  const withAddress = customers.filter((customer) => customer.address).length;
  const withCoordinates = customers.filter(
    (customer) => customer.latitude != null && customer.longitude != null,
  );
  const selected = customers.find((customer) => customer.id === selectedId);

  return (
    <section className="square-map-panel" aria-label="Square customer coverage map">
      <div className="square-map-heading">
        <div>
          <span className="eyebrow">Coverage</span>
          <h3>Verified customer locations</h3>
          <p>
            Addresses are read from Square. Pins appear only when Square provides verified
            coordinates, so this view never guesses a customer’s position. Showing the{" "}
            {customers.length}
            records loaded so far; use “Load more” below to expand the view.
          </p>
        </div>
        <div className="square-map-stats">
          <span>
            <strong>{withAddress}</strong> with address
          </span>
          <span>
            <strong>{withCoordinates.length}</strong> pinned
          </span>
        </div>
      </div>

      <div className="square-map-canvas">
        <div className="square-map-label">
          <MapIcon /> Massachusetts coverage
        </div>
        {withCoordinates.length ? (
          withCoordinates.map((customer) => {
            const position = mapPosition(customer.latitude!, customer.longitude!);
            return (
              <button
                key={customer.id}
                className={`square-map-pin ${selectedId === customer.id ? "selected" : ""}`}
                style={{ left: `${position.x}%`, top: `${position.y}%` }}
                aria-label={`View ${customer.name}`}
                onClick={() => setSelectedId(customer.id)}
              >
                <MapPin />
              </button>
            );
          })
        ) : (
          <div className="square-map-empty">
            <MapPin />
            <strong>No verified coordinates yet</strong>
            <span>
              Square supplied {withAddress ? "address records" : "no address records"}, but not map
              coordinates. Pins will appear automatically if coordinate data is added to the
              provider response.
            </span>
          </div>
        )}
      </div>

      {selected ? (
        <div className="square-map-selection" role="status">
          <div>
            <strong>{selected.name}</strong>
            <span>{formatCustomerAddress(selected.address)}</span>
          </div>
          <button
            className="icon-button"
            aria-label="Close selected customer"
            onClick={() => setSelectedId(undefined)}
          >
            <X />
          </button>
        </div>
      ) : null}
    </section>
  );
}

function ClientPanel({
  clientId,
  canWrite,
  onClose,
  onChanged,
  onCreated,
}: {
  clientId?: string;
  canWrite: boolean;
  onClose: () => void;
  onChanged: () => void;
  onCreated: (id: string) => void;
}) {
  const [client, setClient] = useState<ClientDetail>();
  const [loading, setLoading] = useState(Boolean(clientId));
  const [failed, setFailed] = useState(false);
  const [editing, setEditing] = useState(!clientId);
  const [addingProperty, setAddingProperty] = useState(false);
  const [editingProperty, setEditingProperty] = useState<Property>();

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      setClient(await getClient(clientId));
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => void load(), [load]);

  return (
    <div className="client-drawer-backdrop" onMouseDown={onClose}>
      <section
        className="client-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={clientId ? "Client profile" : "Add client"}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="drawer-header">
          <div>
            <span className="eyebrow lime">{clientId ? "Client profile" : "New client"}</span>
            <h2>{client?.name ?? (clientId ? "Loading…" : "Add a client")}</h2>
          </div>
          <button className="icon-button" aria-label="Close client profile" onClick={onClose}>
            <X />
          </button>
        </header>

        <div className="drawer-body">
          {loading ? (
            <div className="table-state">
              <LoaderCircle className="spin" /> Loading profile…
            </div>
          ) : null}
          {failed ? <ClientLoadError onRetry={() => void load()} /> : null}

          {editing ? (
            <ClientForm
              client={client}
              onCancel={client ? () => setEditing(false) : onClose}
              onSaved={(saved) => {
                setClient(saved);
                setEditing(false);
                onChanged();
                if (!clientId) onCreated(saved.id);
              }}
            />
          ) : client ? (
            <>
              <section className="profile-summary">
                <div className="profile-title-row">
                  <ClientStatusBadge status={client.status} />
                  {canWrite ? (
                    <button className="text-button" onClick={() => setEditing(true)}>
                      <Pencil /> Edit client
                    </button>
                  ) : null}
                </div>
                <a href={`mailto:${client.email}`}>
                  <Mail /> {client.email}
                </a>
                <a href={`tel:${client.phone.replace(/[^+\d]/g, "")}`}>
                  <Phone /> {client.phone}
                </a>
                {client.secondaryPhone ? (
                  <a href={`tel:${client.secondaryPhone.replace(/[^+\d]/g, "")}`}>
                    <Phone /> {client.secondaryPhone}
                  </a>
                ) : null}
                {client.notes ? <p className="client-notes">{client.notes}</p> : null}
              </section>

              <section className="property-section">
                <div className="property-heading">
                  <div>
                    <span className="eyebrow">Properties</span>
                    <h3>{client.properties.length || "No"} saved properties</h3>
                  </div>
                  {canWrite ? (
                    <button
                      className="secondary-button small-action"
                      onClick={() => setAddingProperty(true)}
                    >
                      <Plus /> Add property
                    </button>
                  ) : null}
                </div>

                {addingProperty ? (
                  <PropertyForm
                    onCancel={() => setAddingProperty(false)}
                    onSave={async (input) => {
                      await createProperty(client.id, input);
                      setAddingProperty(false);
                      await load();
                      onChanged();
                    }}
                  />
                ) : null}

                <div className="property-list">
                  {client.properties.map((property) =>
                    editingProperty?.id === property.id ? (
                      <PropertyForm
                        key={property.id}
                        property={property}
                        onCancel={() => setEditingProperty(undefined)}
                        onSave={async (input) => {
                          await updateProperty(client.id, property.id, input);
                          setEditingProperty(undefined);
                          await load();
                        }}
                      />
                    ) : (
                      <PropertyCard
                        key={property.id}
                        property={property}
                        canWrite={canWrite}
                        onEdit={() => setEditingProperty(property)}
                      />
                    ),
                  )}
                </div>
              </section>
            </>
          ) : null}
        </div>
      </section>
    </div>
  );
}

function ClientForm({
  client,
  onCancel,
  onSaved,
}: {
  client?: ClientDetail;
  onCancel: () => void;
  onSaved: (client: ClientDetail) => void;
}) {
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSending(true);
    setFailed(false);
    const fields = new FormData(event.currentTarget);
    const input: ClientInput = {
      name: String(fields.get("name")),
      email: String(fields.get("email")),
      phone: String(fields.get("phone")),
      secondaryPhone: nullableText(fields.get("secondaryPhone")),
      status: String(fields.get("status")) as ClientStatus,
      notes: nullableText(fields.get("notes")),
    };
    try {
      onSaved(client ? await updateClient(client.id, input) : await createClient(input));
    } catch {
      setFailed(true);
      setSending(false);
    }
  }

  return (
    <form className="record-form" onSubmit={submit}>
      <div className="form-grid">
        <label className="wide-field">
          Full name
          <input name="name" defaultValue={client?.name} maxLength={120} required />
        </label>
        <label>
          Email
          <input name="email" type="email" defaultValue={client?.email} maxLength={254} required />
        </label>
        <label>
          Primary phone
          <input name="phone" type="tel" defaultValue={client?.phone} maxLength={30} required />
        </label>
        <label>
          Secondary phone
          <input
            name="secondaryPhone"
            type="tel"
            defaultValue={client?.secondaryPhone ?? ""}
            maxLength={30}
          />
        </label>
        <label>
          Status
          <select name="status" defaultValue={client?.status ?? "ACTIVE"}>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
            <option value="ARCHIVED">Archived</option>
          </select>
        </label>
        <label className="wide-field">
          Private notes
          <textarea name="notes" defaultValue={client?.notes ?? ""} maxLength={10000} rows={4} />
        </label>
      </div>
      {failed ? (
        <p className="form-error">We couldn’t save this client. Review the fields and try again.</p>
      ) : null}
      <div className="form-actions">
        <button type="button" className="secondary-button" onClick={onCancel}>
          Cancel
        </button>
        <button className="primary-button compact-action" disabled={sending}>
          {sending ? <LoaderCircle className="spin" /> : null} Save client
        </button>
      </div>
    </form>
  );
}

function PropertyForm({
  property,
  onCancel,
  onSave,
}: {
  property?: Property;
  onCancel: () => void;
  onSave: (input: PropertyInput) => Promise<void>;
}) {
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSending(true);
    setFailed(false);
    const fields = new FormData(event.currentTarget);
    const input: PropertyInput = {
      propertyType: String(fields.get("propertyType")) as PropertyType,
      address: String(fields.get("address")),
      addressLine2: nullableText(fields.get("addressLine2")),
      city: String(fields.get("city")),
      state: String(fields.get("state")),
      zipCode: String(fields.get("zipCode")),
      bedrooms: nullableNumber(fields.get("bedrooms")),
      bathrooms: nullableNumber(fields.get("bathrooms")),
      floors: nullableNumber(fields.get("floors")),
      squareFeet: nullableNumber(fields.get("squareFeet")),
      accessNotes: nullableText(fields.get("accessNotes")),
      parkingNotes: nullableText(fields.get("parkingNotes")),
      pets: nullableText(fields.get("pets")),
      specialNotes: nullableText(fields.get("specialNotes")),
    };
    try {
      await onSave(input);
    } catch {
      setFailed(true);
      setSending(false);
    }
  }

  return (
    <form className="record-form property-form" onSubmit={submit}>
      <div className="form-grid">
        <label>
          Property type
          <select name="propertyType" defaultValue={property?.propertyType ?? "RESIDENTIAL"}>
            <option value="RESIDENTIAL">Residential</option>
            <option value="COMMERCIAL">Commercial</option>
            <option value="OTHER">Other</option>
          </select>
        </label>
        <label className="wide-field">
          Street address
          <input name="address" defaultValue={property?.address} maxLength={180} required />
        </label>
        <label className="wide-field">
          Apartment, suite, or unit
          <input name="addressLine2" defaultValue={property?.addressLine2 ?? ""} maxLength={120} />
        </label>
        <label>
          City
          <input name="city" defaultValue={property?.city} maxLength={120} required />
        </label>
        <label>
          State
          <input name="state" defaultValue={property?.state ?? "MA"} maxLength={2} required />
        </label>
        <label>
          ZIP code
          <input
            name="zipCode"
            inputMode="numeric"
            defaultValue={property?.zipCode}
            maxLength={10}
            required
          />
        </label>
        <label>
          Bedrooms
          <input
            name="bedrooms"
            type="number"
            min="0"
            max="100"
            defaultValue={property?.bedrooms ?? ""}
          />
        </label>
        <label>
          Bathrooms
          <input
            name="bathrooms"
            type="number"
            min="0"
            max="100"
            step="0.5"
            defaultValue={property?.bathrooms ?? ""}
          />
        </label>
        <label>
          Floors
          <input
            name="floors"
            type="number"
            min="0"
            max="100"
            defaultValue={property?.floors ?? ""}
          />
        </label>
        <label>
          Square feet
          <input
            name="squareFeet"
            type="number"
            min="0"
            max="10000000"
            defaultValue={property?.squareFeet ?? ""}
          />
        </label>
        <label className="wide-field">
          Access instructions
          <textarea name="accessNotes" defaultValue={property?.accessNotes ?? ""} rows={2} />
        </label>
        <label className="wide-field">
          Parking notes
          <textarea name="parkingNotes" defaultValue={property?.parkingNotes ?? ""} rows={2} />
        </label>
        <label className="wide-field">
          Pets
          <textarea name="pets" defaultValue={property?.pets ?? ""} rows={2} />
        </label>
        <label className="wide-field">
          Special notes
          <textarea name="specialNotes" defaultValue={property?.specialNotes ?? ""} rows={3} />
        </label>
      </div>
      {failed ? (
        <p className="form-error">
          We couldn’t save this property. Review the fields and try again.
        </p>
      ) : null}
      <div className="form-actions">
        <button type="button" className="secondary-button" onClick={onCancel}>
          Cancel
        </button>
        <button className="primary-button compact-action" disabled={sending}>
          {sending ? <LoaderCircle className="spin" /> : null} Save property
        </button>
      </div>
    </form>
  );
}

function PropertyCard({
  property,
  canWrite,
  onEdit,
}: {
  property: Property;
  canWrite: boolean;
  onEdit: () => void;
}) {
  return (
    <article className="property-card">
      <div className="property-icon">
        <Home />
      </div>
      <div className="property-copy">
        <span className="property-type">{humanize(property.propertyType)}</span>
        <h4>{property.address}</h4>
        <p>
          {property.addressLine2 ? `${property.addressLine2}, ` : ""}
          {property.city}, {property.state} {property.zipCode}
        </p>
        <div className="property-facts">
          {property.bedrooms != null ? <span>{property.bedrooms} beds</span> : null}
          {property.bathrooms != null ? <span>{property.bathrooms} baths</span> : null}
          {property.squareFeet != null ? (
            <span>{property.squareFeet.toLocaleString()} sq ft</span>
          ) : null}
          {property.regionName ? (
            <span>
              <MapPin /> {property.regionName}
            </span>
          ) : null}
        </div>
      </div>
      {canWrite ? (
        <button
          className="icon-button property-edit"
          aria-label={`Edit ${property.address}`}
          onClick={onEdit}
        >
          <Pencil />
        </button>
      ) : null}
    </article>
  );
}

function ClientStatusBadge({ status }: { status: ClientStatus }) {
  return <span className={`status client-status-${status.toLowerCase()}`}>{humanize(status)}</span>;
}

function ClientLoadError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="load-error" role="alert">
      We couldn’t refresh client records.<button onClick={onRetry}>Try again</button>
    </div>
  );
}

function SquareCustomerLoadError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="load-error" role="alert">
      We couldn’t refresh Square customers.<button onClick={onRetry}>Try again</button>
    </div>
  );
}

function nullableText(value: FormDataEntryValue | null) {
  const text = String(value ?? "").trim();
  return text || null;
}

function nullableNumber(value: FormDataEntryValue | null) {
  const text = String(value ?? "").trim();
  return text ? Number(text) : null;
}

function humanize(value: string) {
  return value.charAt(0) + value.slice(1).toLowerCase().replaceAll("_", " ");
}

function formatShortDate(value: string) {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(value));
}

function formatCustomerDate(value: string | null) {
  if (!value) return "—";
  return formatShortDate(value);
}

function formatCustomerAddress(address: SquareCustomer["address"]) {
  if (!address) return "—";
  const locality = [address.city, address.state].filter(Boolean).join(", ");
  return [address.line1, locality, address.postalCode].filter(Boolean).join(" · ") || "—";
}

function mapPosition(latitude: number, longitude: number) {
  const x = ((longitude - -73.5) / (-69.8 - -73.5)) * 100;
  const y = (1 - (latitude - 41.2) / (42.9 - 41.2)) * 100;
  return { x: Math.min(96, Math.max(4, x)), y: Math.min(92, Math.max(8, y)) };
}
